import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { useDispatch } from 'react-redux';
import useLanguage from '../../../../hooks/useLanguage';
import { Language } from '../../../../types/requests';
import { setLanguage } from '../../../../redux/slices/applicationSlice';
import { EmailPreferenceType } from '../../../../types/misc';
import { getUserPreferences, updateUserPreferences } from '../../../../services/profileService';
import KnobToggle from '../../../../components/misc/KnobToggle';
import Button from '../../../../components/misc/Button';
import UnavailableFeature from '../../../../components/misc/UnavailableFeature';

type TabId = 'account' | 'email' | 'calendar';

export default function SettingsPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const currentLanguage = useLanguage();

  const [activeTab, setActiveTab] = useState<TabId>('account');
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Account tab state
  const [selectedLanguage, setSelectedLanguage] = useState<Language>(currentLanguage);
  const [languageChanged, setLanguageChanged] = useState(false);

  // Email preferences state
  const [emailPreferences, setEmailPreferences] = useState<EmailPreferenceType[]>([]);
  const [originalEmailPreferences, setOriginalEmailPreferences] = useState<EmailPreferenceType[]>([]);
  const [emailPreferencesChanged, setEmailPreferencesChanged] = useState(false);

  const tabs = [
    { id: 'account' as TabId, label: t('page.settings.tabs.account') },
    { id: 'email' as TabId, label: t('page.settings.tabs.emailPreferences') },
    { id: 'calendar' as TabId, label: t('page.settings.tabs.calendarSync') },
  ];

  // Load email preferences
  useEffect(() => {
    if (activeTab !== 'email') return;

    setIsLoading(true);
    getUserPreferences()
      .then((optOuts) => {
        const optOutList = Array.isArray(optOuts) ? optOuts : [];
        const optOutsNorm = optOutList.filter((o) => typeof o === 'string').map((s) => s.trim().toUpperCase());

        const allPrefs = Object.values(EmailPreferenceType) as EmailPreferenceType[];
        const allPrefsNormMap = new Map<string, EmailPreferenceType>();
        allPrefs.forEach((p) => allPrefsNormMap.set((p as string).trim().toUpperCase(), p));

        const matchedOptOuts = optOutsNorm.map((o) => allPrefsNormMap.get(o)).filter(Boolean) as EmailPreferenceType[];
        const matchedSet = new Set(matchedOptOuts);
        const enabled = allPrefs.filter((p) => !matchedSet.has(p));

        setEmailPreferences(enabled);
        setOriginalEmailPreferences([...enabled]);
      })
      .catch((error) => {
        console.error('Failed to load email preferences:', error);
      })
      .finally(() => setIsLoading(false));
  }, [activeTab]);

  // Track language changes
  useEffect(() => {
    setLanguageChanged(selectedLanguage !== currentLanguage);
  }, [selectedLanguage, currentLanguage]);

  // Track email preference changes
  useEffect(() => {
    const a = emailPreferences ?? [];
    const b = originalEmailPreferences ?? [];

    if (a.length !== b.length) {
      setEmailPreferencesChanged(true);
      return;
    }

    const bSet = new Set(b);
    const changed = a.some((pref) => !bSet.has(pref));
    setEmailPreferencesChanged(changed);
  }, [emailPreferences, originalEmailPreferences]);

  const handleTabChange = (tabId: TabId) => {
    setActiveTab(tabId);
  };

  const handleLanguageChange = (lang: Language) => {
    setSelectedLanguage(lang);
  };

  const handleSaveLanguage = () => {
    dispatch(setLanguage(selectedLanguage));
    i18n.changeLanguage(selectedLanguage);
    setLanguageChanged(false);
  };

  const toggleEmailPreference = (preference: EmailPreferenceType) => {
    setEmailPreferences((prev) => {
      if (!prev) return [preference];
      if (prev.includes(preference)) {
        return prev.filter((p) => p !== preference);
      }
      return [...prev, preference];
    });
  };

  const handleSaveEmailPreferences = () => {
    setIsSaving(true);
    const allPrefs = Object.values(EmailPreferenceType) as EmailPreferenceType[];
    const optOuts = allPrefs.filter((p) => !emailPreferences.includes(p));

    updateUserPreferences(optOuts)
      .then(() => {
        setOriginalEmailPreferences(emailPreferences);
        setEmailPreferencesChanged(false);
      })
      .catch((error) => {
        console.error('Failed to save email preferences:', error);
      })
      .finally(() => setIsSaving(false));
  };

  const handleCancelEmailPreferences = () => {
    setEmailPreferences(originalEmailPreferences);
    setEmailPreferencesChanged(false);
  };

  const emailPreferenceLabels: Partial<Record<EmailPreferenceType, { title: string; description: string }>> = {
    [EmailPreferenceType.INTRODUCTIONEMAIL]: {
      title: t('page.settings.emailPreferences.introduction.title'),
      description: t('page.settings.emailPreferences.introduction.description'),
    },
    [EmailPreferenceType.NEWCONNECTIONEMAIL]: {
      title: t('page.settings.emailPreferences.newConnection.title'),
      description: t('page.settings.emailPreferences.newConnection.description'),
    },
    [EmailPreferenceType.DATAREQUESTEMAIL]: {
      title: t('page.settings.emailPreferences.dataRequest.title'),
      description: t('page.settings.emailPreferences.dataRequest.description'),
    },
    [EmailPreferenceType.COMMUNITYEMAIL]: {
      title: t('page.settings.emailPreferences.community.title'),
      description: t('page.settings.emailPreferences.community.description'),
    },
    [EmailPreferenceType.CONNECTION_REQUESTS]: {
      title: t('page.settings.emailPreferences.connectionRequests.title'),
      description: t('page.settings.emailPreferences.connectionRequests.description'),
    },
    [EmailPreferenceType.INTRODUCTIONS]: {
      title: t('page.settings.emailPreferences.introductions.title'),
      description: t('page.settings.emailPreferences.introductions.description'),
    },
    [EmailPreferenceType.EVENT_INVITES]: {
      title: t('page.settings.emailPreferences.eventInvites.title'),
      description: t('page.settings.emailPreferences.eventInvites.description'),
    },
    [EmailPreferenceType.MARKETING_AUTOMATION]: {
      title: t('page.settings.emailPreferences.marketingAutomation.title'),
      description: t('page.settings.emailPreferences.marketingAutomation.description'),
    },
  };

  const getPreferenceLabel = (preference: EmailPreferenceType) =>
    emailPreferenceLabels[preference] ?? {
      title: t(`page.settings.emailPreferences.${preference}`, { defaultValue: preference }),
      description: '',
    };

  return (
    <div className="flex min-h-screen flex-col p-3 lg:p-6">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="mb-2 text-2xl font-bold text-primary sm:text-3xl">{t('page.settings.title')}</h1>
          <p className="text-sm text-details/60">{t('page.settings.description')}</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-6">
        <nav className="border-secondary-100 flex flex-nowrap gap-6 overflow-x-auto border-b px-3 pb-2 sm:mx-0 sm:gap-12 sm:px-0">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => handleTabChange(tab.id)}
              className={classNames('whitespace-nowrap border-b-2 px-4 py-2 text-sm font-medium transition-colors', {
                'border-primary text-primary': activeTab === tab.id,
                'border-transparent text-details/60 hover:text-primary': activeTab !== tab.id,
              })}>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="w-full">
        {/* Account Tab */}
        {activeTab === 'account' && (
          <div>
            <h2 className="text-lg font-semibold">{t('page.settings.account.languageTitle')}</h2>
            <p className="mt-1 text-sm text-details/60">{t('page.settings.account.languageDescription')}</p>

            <div className="space-y-3 py-4">
              <div
                role="button"
                tabIndex={0}
                onClick={() => handleLanguageChange(Language.EN)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleLanguageChange(Language.EN);
                }}
                className={classNames(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors',
                  {
                    'border-primary bg-secondary-200': selectedLanguage === Language.EN,
                    'hover:border-primary': selectedLanguage !== Language.EN,
                  },
                )}
                aria-pressed={selectedLanguage === Language.EN}>
                <div className="flex-1">
                  <div className="font-medium text-primary-900">English</div>
                  <div className="text-sm text-details/60">English (United States)</div>
                </div>
              </div>

              <div
                role="button"
                tabIndex={0}
                onClick={() => handleLanguageChange(Language.NL)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') handleLanguageChange(Language.NL);
                }}
                className={classNames(
                  'flex cursor-pointer items-center gap-3 rounded-lg border p-4 transition-colors',
                  {
                    'border-primary bg-secondary-200': selectedLanguage === Language.NL,
                    'hover:border-primary': selectedLanguage !== Language.NL,
                  },
                )}
                aria-pressed={selectedLanguage === Language.NL}>
                <div className="flex-1">
                  <div className="font-medium text-primary-900">Nederlands</div>
                  <div className="text-sm text-details/60">Dutch (Netherlands)</div>
                </div>
              </div>

              <div className="border-secondary-100 mt-6 flex gap-3 border-t pt-4">
                <Button variant="primary" onClick={handleSaveLanguage} disabled={!languageChanged || isSaving}>
                  {isSaving ? t('general.saving') : t('general.save')}
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Email Preferences Tab */}
        {activeTab === 'email' && (
          <div>
            <h2 className="text-lg font-semibold">{t('page.settings.emailPreferences.title')}</h2>
            <p className="mt-1 text-sm text-details/60">{t('page.settings.emailPreferences.description')}</p>

            {isLoading ? (
              <div className="p-8 text-center text-details/60">{t('general.loading')}</div>
            ) : (
              <div className="space-y-3 py-4">
                {Object.values(EmailPreferenceType).map((preference) => {
                  const label = getPreferenceLabel(preference);

                  return (
                    <div
                      key={preference}
                      className="border-secondary-100 flex items-center justify-between rounded-lg border p-4 transition-colors">
                      <div className="flex-1">
                        <div className="font-medium text-primary-900">{label.title}</div>
                        <div className="mt-1 text-sm text-details/60">{label.description}</div>
                      </div>
                      <KnobToggle
                        value={emailPreferences?.includes(preference) ?? false}
                        onChange={() => toggleEmailPreference(preference)}
                      />
                    </div>
                  );
                })}

                <div className="border-secondary-100 flex gap-3 border-t pt-4">
                  <Button
                    variant="primary"
                    onClick={handleSaveEmailPreferences}
                    disabled={!emailPreferencesChanged || isSaving || isLoading}>
                    {isSaving ? t('general.saving') : t('general.save')}
                  </Button>
                  {emailPreferencesChanged && (
                    <Button variant="secondary" onClick={handleCancelEmailPreferences} disabled={isSaving}>
                      {t('general.cancel')}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Calendar Sync Tab (Placeholder) */}
        {activeTab === 'calendar' && <UnavailableFeature />}
      </div>
    </div>
  );
}
